import React, { useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Save, Loader2, KeyRound, User, Stethoscope } from "lucide-react";
import { errorDetail } from "@/lib/utils";
import { PLATFORM } from "@/lib/platform";

export default function AdminSettings() {
  const { user } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const runDiagnostics = async () => {
    setDiagBusy(true);
    setDiag(null);
    try {
      const { data } = await api.get("/diagnostics");
      setDiag(data);
    } catch (e) {
      setDiag({ error: errorDetail(e) || e.message });
    } finally {
      setDiagBusy(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (newPw.length < 8) {
      setMsg({ kind: "error", text: "New password must be at least 8 characters." });
      return;
    }
    if (newPw !== confirmPw) {
      setMsg({ kind: "error", text: "New password and confirmation do not match." });
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      setMsg({ kind: "success", text: "Password updated. You will stay signed in for now." });
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (e) {
      setMsg({ kind: "error", text: errorDetail(e) || "Failed to update password." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="hed-overline">Account</div>
        <h1 className="mt-1 font-display text-2xl font-bold text-heda-ink md:text-[30px]">Settings</h1>
      </div>

      <div className="grid max-w-4xl items-start gap-6 lg:grid-cols-2">
        {/* Profile card */}
        <div className="heda-panel p-5 md:p-7">
          <h2 className="flex items-center gap-2 font-display text-[18px] font-bold text-heda-ink">
            <User size={18} className="text-heda-gold" /> Your profile
          </h2>
          <div className="hairline my-4" />
          <div className="space-y-3">
            <div>
              <div className="heda-label">Email</div>
              <div className="text-sm font-semibold text-heda-ink [overflow-wrap:anywhere]">{user?.email}</div>
            </div>
            <div>
              <div className="heda-label">Role</div>
              <span className="rounded-full bg-heda-gold/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-heda-gold">
                {user?.role || "staff"}
              </span>
            </div>
          </div>
        </div>

        {/* Change password card */}
        <div className="heda-panel p-5 md:p-7">
          <h2 className="flex items-center gap-2 font-display text-[18px] font-bold text-heda-ink">
            <KeyRound size={18} className="text-heda-gold" /> Change password
          </h2>
          <div className="hairline my-4" />
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label="Current password">
              <input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                required
                data-testid="current-password-input"
                className="heda-input"
              />
            </Field>
            <Field label="New password (min 8 chars)">
              <input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                required
                data-testid="new-password-input"
                className="heda-input"
              />
            </Field>
            <Field label="Confirm new password">
              <input
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                required
                data-testid="confirm-password-input"
                className="heda-input"
              />
            </Field>

            {msg && (
              <div
                data-testid="password-message"
                className={
                  msg.kind === "success"
                    ? "rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700"
                    : "heda-error"
                }
              >
                {msg.text}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="change-password-button"
              className="heda-btn w-full"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Update password
            </button>
          </form>
        </div>
      </div>

      {/* System diagnostics (owner-only; server operators only, so not on the platform) */}
      {user?.role === "owner" && !PLATFORM && (
        <div className="heda-panel mt-6 max-w-4xl p-5 md:p-7">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="flex items-center gap-2 font-display text-[18px] font-bold text-heda-ink">
              <Stethoscope size={18} className="text-heda-gold" /> System diagnostics
            </h2>
            <button
              onClick={runDiagnostics}
              disabled={diagBusy}
              data-testid="run-diagnostics-button"
              className="heda-pill"
            >
              {diagBusy ? <Loader2 size={13} className="animate-spin" /> : <Stethoscope size={13} />}
              Run check
            </button>
          </div>
          <p className="mb-4 text-[13px] text-heda-sub">
            Verifies environment variables and live AI connectivity. If extraction is failing on
            production, run this here AND on your production URL — anything red is your problem.
          </p>
          {diag && (
            <pre
              data-testid="diagnostics-output"
              className="overflow-x-auto whitespace-pre-wrap rounded-xl border border-heda-line bg-heda-cream/50 p-3 text-[11px] text-heda-sub"
            >
              {JSON.stringify(diag, null, 2)}
            </pre>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="heda-label">{label}</label>
      {children}
    </div>
  );
}
